// Report palette for the exported PropertyPath PDF.
//
// Added 2026-07-25. The report carries the AGENT's name, logo, brokerage and
// website, and then printed all of it in frozen house colors — every color in
// generatePDF() was a hardcoded literal. Same defect fixed in STRcalc v5.9.8,
// RentOrOwn v2.8.0 and SellerCalc v9.8.0.

export const HOUSE_GOLD = '#9A7820';
export const HOUSE_ACCENT = '#167A5E';

// Validate AND expand 3-digit hex to 6. These values are interpolated into
// style="" attributes and concatenated with alpha suffixes, so a malformed or
// short value from the brand payload would emit an invalid color and silently
// corrupt the print document rather than fail loudly.
export function brandHex(v, fb) {
  const t = typeof v === 'string' ? v.trim() : '';
  if (/^#[0-9a-fA-F]{6}$/.test(t)) return t.toUpperCase();
  if (/^#[0-9a-fA-F]{3}$/.test(t)) return ('#' + t[1] + t[1] + t[2] + t[2] + t[3] + t[3]).toUpperCase();
  return fb;
}

// "Redmean" weighted distance, not plain RGB euclid. Plain euclid badly
// under-rates grey-vs-saturated pairs. Range roughly 0..765.
export function colorDist(a, b) {
  const ch = (h, i) => parseInt(h.substr(i, 2), 16);
  const r1 = ch(a, 1), r2 = ch(b, 1), rm = (r1 + r2) / 2;
  const dr = r1 - r2, dg = ch(a, 3) - ch(b, 3), db = ch(a, 5) - ch(b, 5);
  return Math.sqrt((2 + rm / 256) * dr * dr + 4 * dg * dg + (2 + (255 - rm) / 256) * db * db);
}

function hslToHex(h, sat, l) {
  const a = sat * Math.min(l, 1 - l);
  const f = (n) => {
    const k = (n + h / 30) % 12;
    return Math.round((l - a * Math.max(-1, Math.min(k - 3, Math.min(9 - k, 1)))) * 255).toString(16).padStart(2, '0');
  };
  return ('#' + f(0) + f(8) + f(4)).toUpperCase();
}

// WCAG contrast against the white printed page. The accent is used as heading
// TEXT and as a button background behind white text, so it has to stay legible
// on paper, not merely differ from the gold.
export function contrastOnWhite(hx) {
  const f = (i) => {
    const c = parseInt(hx.substr(i, 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return 1.05 / (0.2126 * f(1) + 0.7152 * f(3) + 0.0722 * f(5) + 0.05);
}

// Full hue wheel at two saturations and four lightnesses, filtered to WCAG AA.
// Rejected alternatives, both measured: a short fixed candidate list cannot
// cover the wheel, and a pure hue complement at fixed S/L produced yellows at
// 2:1 on white, i.e. unreadable text.
const PALETTE = (function () {
  const out = [];
  for (let h = 0; h < 360; h += 30)
    for (const sat of [0.55, 0.75])
      for (const l of [0.18, 0.24, 0.30, 0.36]) {
        const c = hslToHex(h, sat, l);
        if (contrastOnWhite(c) >= 4.5) out.push(c);
      }
  return out;
})();

// The accent sits directly beside the brand gold (CTA box border against the
// gold headings, buttons against gold links), so it must never collapse into
// it. Threshold 200: house gold vs house green scores 226, so an unbranded
// report keeps the house green exactly and only brand colors that would crowd
// the green get reassigned.
export function pickContrast(gold) {
  if (colorDist(HOUSE_ACCENT, gold) >= 200) return HOUSE_ACCENT;
  return PALETTE.reduce((best, c) => colorDist(c, gold) > colorDist(best, gold) ? c : best);
}

// accent_color || primary_color || house gold. accent_color alone is not
// enough: live agents exist with an empty accent_color, which is exactly how
// this class of bug shipped in the first place.
export function resolveReportPalette(brandIdentity) {
  const bi = brandIdentity || {};
  const gold = brandHex(bi.accent_color, brandHex(bi.primary_color, HOUSE_GOLD));
  return { gold, accent: pickContrast(gold) };
}
